import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import InputMask from 'react-input-mask';
import api from '../../api';
/* actions */
import { editOccurrences } from '../../redux/actions/occurrences';
import { getAllDepartments } from '../../redux/actions/departments';
import { getAllTypeOccurrences } from '../../redux/actions/typeOccurrences';
import { getAllUsers } from '../../redux/actions/users';
import { getAllClients } from '../../redux/actions/clients';
import { getAllPets } from '../../redux/actions/pets';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
/* internal components */
import Clients from './Clients';
/* form-validation */
import schema from '../../form-validation/occurrence';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';
import sortable from '../../utils/sortable';
import handleChangeDefault from '../../utils/handleChangeDefault';
import ptBR from 'date-fns/locale/pt-BR';
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';
import translateSeverityOccurrence from '../../utils/translateSeverityOccurrence';
registerLocale('ptBR', ptBR);

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    _id: id || '',
    day: new Date(),
    hour: new Date().toLocaleTimeString(),
    department: '',
    typeOccurrence: '',
    description: '',
    employees: [],
    clients: [],
    pets: [],
    company: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, setError, setValue } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const occurrences = useSelector(state => state.occurrences);
  const [data, setData] = useState(INITIAL_VALUES);
  const departments = useSelector(state => state.departments.data);
  const users = useSelector(state => state.users.data);
  const clients = useSelector(state => state.clients.data);
  const pets = useSelector(state => state.pets.data);
  const typeOccurrences = useSelector(state => state.typeOccurrences.data);
  const [departmentOptions, setDepartmentOptions] = useState([firstOption]);
  const [typeOccurrenceOptions, setTypeOccurrenceOptions] = useState([
    firstOption
  ]);
  const INITIAL_OPTIONS = { employees: [], clients: [], pets: [] };
  const [selectedOptions, setSelectedOptions] = useState(INITIAL_OPTIONS);
  const [usrs, setUsrs] = useState([]);
  const [pts, setPts] = useState([]);

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllTypeOccurrences({ company }));
    dispatch(getAllUsers({ company }));
    dispatch(getAllClients({ company }));
    dispatch(getAllPets({ company }));
  }, [dispatch]);

  useEffect(() => {
    (async function () {
      try {
        const params = { _id: id };
        const { data } = await api().get('/api/occurrences', { params });
        if (data[0]) {
          const newData = {
            ...INITIAL_VALUES,
            ...data[0],
            day: new Date(data[0].date),
            hour: new Date(data[0].date).toLocaleTimeString(),
            department: data[0].department._id,
            typeOccurrence: data[0].typeOccurrence._id
          };
          delete newData.date;
          setData(newData);
        }
      } catch (err) {
        console.error('Error: ', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (departments.length) {
      const options = departments
        .map(department => ({
          value: department._id,
          label: `${department.name}`,
          sortValue: `${department.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setDepartmentOptions([...options]);
    }
  }, [departments, setDepartmentOptions]);

  useEffect(() => {
    if (typeOccurrences.length) {
      const options = typeOccurrences
        .map(typeOccurrence => ({
          value: typeOccurrence._id,
          label: `${typeOccurrence.name} - ${translateSeverityOccurrence(
            typeOccurrence.classification
          )}`,
          sortValue: `${typeOccurrence.name} - ${translateSeverityOccurrence(
            typeOccurrence.classification
          )}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setTypeOccurrenceOptions([...options]);
    }
  }, [typeOccurrences, setTypeOccurrenceOptions]);

  useEffect(() => {
    if (users.length) {
      const usrs = users
        .map(usr => ({
          value: usr._id,
          text: `${usr.name} | Dpto: ${usr.department.name}`,
          sortValue: usr.name
        }))
        .sort(sortable);

      setUsrs([...usrs]);
    }
  }, [users, setUsrs]);

  useEffect(() => {
    if (pets.length) {
      const pts = pets
        .map(pet => ({
          value: pet._id,
          text: `${pet.name}`,
          sortValue: pet.name,
          responsibles: pet.responsibles
        }))
        .sort(sortable);

      setPts([...pts]);
    }
  }, [pets, setPts]);

  useEffect(() => {
    if (data.employees.length) {
      const users = usrs.filter(option =>
        data.employees.includes(option.value)
      );
      const pets = pts.filter(option => data.pets.includes(option.value));
      setSelectedOptions({ ...selectedOptions, employees: users, pets });
    }
  }, [data.employees, data.pets, usrs, pts, setSelectedOptions]);

  useEffect(() => {
    if (!data.clients.length) {
      setSelectedOptions({ ...selectedOptions, pets: [] });
    }
  }, [data.clients]);

  const submit = () => {
    const newData = { ...data };
    const [hours, minutes] = data.hour.split(':');
    const date = new Date(data.day);
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    delete newData.hour;
    delete newData.day;
    newData.date = date;
    dispatch(editOccurrences(newData, submitButton.current));
  };

  return (
    <div className="container-fluid">
      {occurrences.editSuccess && (
        <AlertMsg type="success" msg="Ocorrência atualizada com sucesso" />
      )}
      {occurrences.errorContent &&
        !occurrences.errorContent.message.includes('400') &&
        occurrences.editError && (
        <AlertMsg
          type="danger"
          msg="Falha durante atualização da ocorrência"
        />
      )}
      {occurrences.errorContent &&
        occurrences.errorContent.message.includes('400') &&
        occurrences.editError && (
        <AlertMsg
          type="danger"
          msg="Uma ocorrência com a data e hora informada já está cadastrada"
        />
      )}
      {!occurrences.editSuccess && !occurrences.editError && (
        <AlertMsg type="info" msg="Área atualização de ocorrências" />
      )}
      <FormHeader title="Atualizar Ocorrência" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="born">Data</label>
              <DatePicker
                className="form-control"
                name="day"
                onChange={date => setData({ ...data, day: date })}
                selected={data.day}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={<input type="text" date={data.day} />}
              />
              {errors.day && errors.day.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="hour">Hora</label>
              <InputMask
                id="hour"
                name="hour"
                maskChar=""
                mask="99:99"
                placeholder="HH:mm"
                className="form-control"
                autoComplete="off"
                value={data.hour}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.hour && errors.hour.type === 'hour' && (
                <ErrorMsgField type="hour" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label
                  className="col-sm-12 d-inline p-0 mb-2"
                  htmlFor="department"
                >
                  Departamento
                </label>
                <Select
                  className="col p-0"
                  name="department"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'department' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={departmentOptions[0]}
                  value={departmentOptions.find(
                    department => department.value === data.department
                  )}
                  options={departmentOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                />
              </div>
              {errors.department && errors.department.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="form-group form-inline mb-0">
            <label
              className="col-sm-12 d-inline p-0 mb-2"
              htmlFor="typeOccurrence"
            >
              Tipo de Ocorrência
            </label>
            <Select
              className="col p-0"
              name="typeOccurrence"
              ref={register}
              onChange={evt =>
                handleChangeSelect(
                  { ...evt, name: 'typeOccurrence' },
                  data,
                  setData,
                  setError,
                  setValue
                )
              }
              defaultValue={typeOccurrenceOptions[0]}
              value={typeOccurrenceOptions.find(
                typeOccurrence => typeOccurrence.value === data.typeOccurrence
              )}
              options={typeOccurrenceOptions}
              isSearchable={true}
              theme={customThemes}
              styles={customStyle}
              filterOption={customFilter}
              noOptionsMessage={() => 'Nenhum registro encontrado'}
            />
          </div>
          {errors.typeOccurrence &&
            errors.typeOccurrence.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="description">Descrição</label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            value={data.description}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <MultiSelect
          avaliableOptions={usrs}
          selectedOptions={selectedOptions.employees}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'employees',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'employees',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Colaboradores Envolvidos"
          name="employees"
          ref={register}
          error={!!errors.employees}
        />
        <Clients clientsData={clients} data={data} setData={setData} />
        <MultiSelect
          avaliableOptions={pts.filter(pet => {
            return pet.responsibles.find(responsible => {
              return data.clients.includes(responsible);
            });
          })}
          selectedOptions={selectedOptions.pets}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'pets',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'pets',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Pets Envolvidos"
          name="pets"
          ref={register}
          error={!!errors.pets}
        />
        <FormFooter url="/occurrences" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
